/* The day's chrome — the frame around the scroller, in every host the day has.
 *
 * `Builder planu - tydzien - programator` draws it: one head row carrying the day arrows, the
 * eyebrow, the name and the close, one subbar carrying the counts and the narrow load bars, and a
 * foot the calendar's editor uses for its Save.
 *
 * ── What LEFT on 13 September 2026 ──────────────────────────────────────────────────────────
 * The fullscreen host's own two rows — `Dzien - pelny ekran`'s breadcrumb bar and session bar, and
 * the wide load module that only stood in the second of them. The owner's day modal is this same
 * head in a bigger box (see `features/plan-day-programming/ui/DayPanelHost`), so those blocks had no host
 * and were removed rather than kept beside one nothing renders. `.loadFill` survived both scales
 * and is still shared.
 */

.spacer {
  flex: 1;
}

/* The dock's `Zapisz` — the SAME control as the top bar's, so it draws from the same place.
 *
 * The TREATMENT is `shared/ui/emberButton.module.css`; this class supplies only the gallery's
 * geometry, as ordinary properties: 32px tall, 14px of side padding, a 15px radius and a 12px/700
 * label.
 *
 * ── The ink is WHITE, deliberately ──────────────────────────────────────────────────────────
 * This comment used to say the opposite, and it went stale under the owner without being rewritten:
 * `--on-accent-grad` was near-black because white measures 2.45–3.54:1 on the gradient's three stops
 * and fails AA at 12px/700, and this note recorded that measurement as the reason. **The owner
 * reversed the token to `#ffffff` on 5 August 2026**, with the measurement and the approval both
 * recorded beside the token itself. The note stayed, and it now reads as an instruction to the next
 * contributor to undo a decision the owner made. It does not. The ink is white on purpose; the
 * failure is known and accepted, and the place that argument is settled is `colors.css`.
 *
 * Keeping the treatment here at all was the second half of the problem: this copy never gained the
 * hover the shared module grew, and it never had `cursor: pointer` — so the dock's Save was the one
 * primary in the app that did not respond to a pointer at all. */
.save {
  height: var(--h-control);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  letter-spacing: normal;
  border-radius: var(--r-button);
}

.titleRow {
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
}

/* `tabIndex={-1}` puts the heading out of the tab order, so the only way focus reaches it is the
 * dock handing it over on open — and Chrome then paints its own ring around the workout's name on
 * arrival, which the mock draws nowhere. Suppressed because it can never be a KEYBOARD cue: nothing
 * a trainer presses lands here, and every control that does keeps its own ring. */
.titleRow:focus,
.titleRow:focus-visible {
  outline: none;
}

.counts {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.loadFill {
  display: block;
  height: 100%;
  border-radius: var(--space-0) var(--radius-bar) var(--radius-bar) var(--space-0);
}

/* ── The head, its subbar and its foot ───────────────────────────────────────────────────── */

.panelHead {
  display: flex;
  flex: none;
  gap: var(--space-9px);
  align-items: center;
  padding: var(--space-10px) var(--space-14px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* `flex: 1` and `min-width: 0` together are what make the title truncate rather than push the two
 * mode buttons off the head — a 604px column has no slack, and a long session name is ordinary. */
.panelIdentity {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.panelIdentity .titleRow {
  min-width: 0;
  overflow: hidden;
}

.panelTitleButton,
.panelTitleInput {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* "PONIEDZIAŁEK · DZIEŃ 1 Z 4", at the mock's own 9.5px on --text-3.
 *
 * Both values are departures from this feature's standing habits — the 12 August 2026 reading floor
 * put captions at 10px, and --text-3 measures 2.80:1 on a card — and both are deliberate: on the
 * programator redraw the mock outranks a house rule about what is VISIBLE, and a contrast objection
 * to a value the canon states is the owner's to make. Flagged rather than silently rounded. */
.panelEyebrow {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--text-3);
  white-space: nowrap;
}

.panelTitleButton {
  padding: var(--space-0);
  font-family: var(--font-head);
  font-size: var(--fs-day-title);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-day-title);
  color: var(--text);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--space-0);
}

.panelTitleButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.panelTitleInput {
  width: 180px;
  padding: var(--space-0);
  font-family: var(--font-head);
  font-size: var(--fs-day-title);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-day-title);
  color: var(--text);
  background: transparent;
  border: 0;
  border-bottom: var(--border-hairline) solid var(--text);
}

.panelTitleInput:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* `flex-wrap` so a day training more groups than the gallery's own four drops its load module onto
 * a second line instead of pushing it off a 604px column. With four bars nothing wraps and the row
 * is the gallery's exactly. */
.panelSubbar {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: var(--space-10px);
  align-items: center;
  padding: var(--space-2) var(--space-14px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}


.panelLoad {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-end;
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.panelLoadBar {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);

  /* 72 rather than the gallery's 66: at the 11px caption floor (system spec, 2 November 2026)
   * "Ramiona" beside a two-digit value no longer fit 66 and ellipsised. */
  width: 72px;
}

.panelLoadHead {
  display: flex;
  gap: var(--space-1);
  align-items: baseline;
  justify-content: space-between;
}

.panelLoadLabel {
  overflow: hidden;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-medium);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panelLoadValue {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* The mock rounds the track on all four corners and clips the fill to it. */
.panelLoadTrack {
  display: block;
  height: 5px;
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

/* The panel host's foot — Save alone, and only where a host hands one over. See `DayPanelFoot`. */
.panelFoot {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--bg-card);
  border-top: var(--border-hairline) solid var(--border);
}

/* The sentence a host hands with its foot's act (`DayFormSave.note`). It takes the room the spacer
 * would, so the button stays at the foot's end. */
.footNote {
  flex: 1;
  min-width: 0;
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* A save that did not go through: the danger ink and its glyph, so the colour is never the only
 * sign. `--danger-text-strong`, not `--danger-ink`: the foot stands on `--bg-card`, warm stone rather
 * than white, where the lighter ink falls under AA (see the danger triplet in `tokens/colors.css`). */
.footNoteAlert {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  color: var(--danger-text-strong);
}

.footNoteGlyph {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  margin-top: var(--space-1);
}

/* ── The gallery's solid ghost button ────────────────────────────────────────────────────── */

/* Its dashed twin went with the panel foot's two actions on 3 August 2026 — the only surface still
 * wanting a dashed add is the warm-up run, and that one is drawn at the day form's own large add
 * weight rather than at this 26px chip's. See `DayForm.module.css` `.warmupAdd`. */
.ghostSolid {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 26px;
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
}

.ghostSolid:hover {
  color: var(--text);
  border-color: var(--text-2);
}

.ghostSolid:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.ghostSolid[aria-disabled='true'] {
  cursor: default;
}

/* ── Controls shared by both heads ───────────────────────────────────────────────────────── */

/* GEOMETRY ONLY. The treatment is `shared/ui/iconButton.module.css`, worn as a second class, and
 * nothing may be declared on both sides — see that file. The gallery draws these at 26px, four
 * under the pointer floor; the shared module grows the target and leaves the painted box alone. */
.iconButton {
  width: 26px;
  height: 26px;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  background: transparent;
  border-radius: var(--radius-mark);
}

/* The programator's own 26 x 24 arrow — one step shorter than the square `.iconButton`, which is
 * what lets the pair sit inside a 10px-padded head without crowding the caption beside it. */
.panelStep {
  height: 24px;
}

/* And its 28px ✕, at the canon's own 10px radius. The host switch beside it takes the SAME box and
 * the same ink rather than a near-copy two pixels off: the two controls stand together at the end of
 * the head and a difference between them would read as a difference in kind. One block, two
 * classes — see the owner's requirement in `DayChrome.tsx`'s header. */
.panelClose,
.modeToggle,
.hostSwitch {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
}

/* …and a gap between them, because sameness is exactly the problem at the end of this head. Two
 * unlabelled 28px squares touching read as one pair of arrows, and the misfire is not symmetrical:
 * hitting the ✕ instead of the toggle closes a day the trainer can reopen, while hitting the toggle
 * instead of the ✕ enters a mode that STICKS for every day they open afterwards.
 *
 * The host toggle takes the SAME gap where it is drawn, because the run of four squares has two
 * halves: the list's two modes, then the day's own chrome. See `.modeGroup`. */
.panelClose,
.hostSwitch {
  margin-inline-start: var(--space-2);
}

/* The two list MODES, held as a pair at the head's tightest gap. It was the only thing telling them
 * apart from the day's own chrome while all four were unlabelled squares; "Superset" carries its
 * word again since 16 September 2026, and the gap still does the work for the fold beside it.
 * Nothing is drawn between them: a rule in this head would be a new object. */
.modeGroup {
  display: flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
}

/* The lighter edge: neither of these is a step through the day.
 *
 * TWO classes in the selector on purpose. `border-color` is the shared treatment's, so a
 * single-class rule here would be one class against one class in a different chunk and the winner
 * would be whichever the bundler emitted last — different on the dev server and in the build. */
.iconButton.closeButton,
.iconButton.hostSwitch {
  border-color: var(--border);
}

/* The ✕'s tertiary ink. It carries no STATE — it is one act with one meaning, and the glyph says
 * which — so the quieter ink is a weight decision and stays. The toggle beside it does not get to
 * borrow it; see below. */
.iconButton.closeButton {
  color: var(--text-3);
}

/* The glyph follows the button's own ink, so hover lifts both together. `currentColor` would be
 * normalised to lowercase by stylelint's `--fix` and then fail its own colour allowlist, which is
 * why this reads the button's state through two rules instead.
 *
 * ── `--text-2`, and the toggle sets NO ink of its own ────────────────────────────────────────
 * The frame and its filled column are the whole of this control's state — which box the day is
 * drawn in — and a graphic carrying meaning owes 3:1. `--text-3` on `--bg-sunken` measures 2.37:1
 * in Flame, and `docs/design/README.md` already records that token as one that "carries no text
 * that must be read"; `--text-2` is 4.93:1. So the button drops its `color` override entirely and
 * takes `.iconButton`'s own `--text-2` and its `--text` hover, and the two SVG parts below follow
 * it by hand. */
.hostSwitchIcon {
  display: block;
  width: 14px;
  height: 14px;
  stroke: var(--text-2);
  fill: none;
}

.hostSwitch:hover .hostSwitchIcon {
  stroke: var(--text);
}

/* The marked column of the toggle's frame — the state, drawn rather than swapped for another glyph.
 * It follows the same ink as the outline so the mark reads as part of the frame and not as a second
 * object inside it. */
.hostSwitchFill {
  fill: var(--text-2);
}

.hostSwitch:hover .hostSwitchFill {
  fill: var(--text);
}

/* ── The panel head's two list modes, and the bar one of them opens ─────────────────────────
 *
 * `Builder planu - tydzien - programator` draws them as a pair of pill buttons on the right of the
 * head: a 28px "Zwiń wszystkie" carrying a chevron, and a 30px "Superset" carrying a chain-link that
 * fills to `--text` while the mode is on.
 *
 * The FOLD is the one that lost its word on 15 September 2026 and kept it off: it takes the same
 * 28px box as the host switch and the ✕ (`.panelClose, .modeToggle, .hostSwitch` above), because a
 * chevron pointing the way the list will move says the whole of it. "Superset" went the same way
 * that day and came back on 16 September — it is a mode that changes what pressing a card does, and
 * that has to be readable without hovering. Its FILL is the canon's own ink-on-ink inversion, which
 * is what keeps the accent out of a mode that is merely on.
 */

.modeToggle {
  /* The glyph's ink, carried as a custom property rather than `currentcolor`: stylelint's value
   * allowlist takes `currentColor` and `value-keyword-case` demands `currentcolor`, so the two
   * rules cannot both be satisfied on a `stroke`. Every state below restates it. */
  --mode-ink: var(--text-2);

  cursor: pointer;
}

.modeToggle:hover {
  --mode-ink: var(--text);
}

.modeButton {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);

  /* Same hand-off as `.modeToggle` above, for the same stylelint reason. */
  --mode-ink: var(--text-2);
}

.modeButton:hover {
  --mode-ink: var(--text);

  color: var(--text);
  border-color: var(--text-2);
}

.modeButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The canon's 30px "Superset", one step taller than the fold beside it. */
.modeButtonWide {
  height: 30px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-micro);
  border-radius: var(--radius-sm);
}

/* The mode is ON. Ink on ink — never the accent, which on this surface marks the JOIN the bar
 * below offers, and two accents for one gesture is the scalpel spent twice. */
.modeButtonOn,
.modeButtonOn:hover {
  --mode-ink: var(--bg-card);

  color: var(--bg-card);
  background: var(--text);
  border-color: var(--text);
}

.modeIcon {
  display: block;
  flex: none;
  width: 14px;
  height: 14px;
  stroke: var(--mode-ink);
  fill: none;
  transition: transform var(--dur-fold) var(--ease);
}

/* The canon draws the chain-link inside the pill one pixel smaller than the chevron standing alone
 * in its 28px box beside it — a glyph with a word after it does not need the same weight as one
 * carrying a control on its own. */
.modeButtonWide .modeIcon {
  width: 13px;
  height: 13px;
}

.modeIconFlipped {
  transform: rotate(180deg);
}

/* The bar the selection mode opens under the head: the instruction, Anuluj, and the join. */
.supersetBar {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  padding: var(--space-9px) var(--space-14px);
  background: var(--bg-inset);
  border-bottom: var(--border-hairline) solid var(--border);
}

.supersetText {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* Two picks in: the sentence stops asking and starts counting, and steps up to full ink with it. */
.supersetTextReady {
  color: var(--text);
}

.supersetCancel {
  flex: none;
  padding: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.supersetCancel:hover {
  color: var(--text);
}

.supersetCancel:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The one accent on this bar, and it marks a real act — the canon's own filled confirm. Refused, it
 * keeps its box and drops to the neutral outline, because a control carrying its reason has to stay
 * focusable and has to stay readable. */
.supersetJoin {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--on-accent);
  white-space: nowrap;
  cursor: pointer;
  background: var(--accent);
  border: var(--border-hairline) solid var(--accent);
  border-radius: var(--radius-mark);
}

.supersetJoin:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.supersetJoin[aria-disabled='true'] {
  color: var(--text-2);
  cursor: default;
  background: transparent;
  border-color: var(--border);
}
